/**
 * 관리 콘솔 › 시스템 설정 › HR 연동 점검 (2026-09-14).
 *
 * 사원번호 하나로 **이 회사의 HR 연동이 실제로 답하는지** 본다. 설정은 바꾸지 않는다.
 *
 * 여기서 지키는 것:
 *   1. ★★★ **읽기 전용** — 부르는 것은 설정 조회(GET)와 사원 조회 시험(POST) 둘뿐이다.
 *   2. ★★★ 결과 네 갈래(찾음 · 없음 · 인증 거절 · 닿지 않음)를 **서로 다른 말**로 적는다 —
 *      「실패했습니다」 한 줄이면 키를 고칠지 방화벽을 볼지 모른다.
 *   3. ★★ 「연동이 꺼져 있음」은 오류가 아니라 점검의 답이다.
 *   4. ★ API 키는 가려진 값만 보인다(서버가 원문을 안 준다).
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { AlertTriangle, CheckCircle, RefreshCw, Search, XCircle } from '../../../icons';
import { ConsoleListScreen } from '../../../components/console-list/ConsoleListScreen';
import {
  fetchHrIntegration, probeHrEmployee,
  type HrIntegrationView, type HrProbeResult,
} from '../../../api/hrIntegration';

const ADAPTER_LABEL: Record<string, string> = {
  standard: '표준 REST',
  sap_hcm: 'SAP HCM',
  douzone_hrd: '더존 HR',
  custom: '사용자 정의',
  none: '없음',
};

function kpiOf(v: HrIntegrationView | null): string {
  if (v === null) return '—';
  if (!v.configured) return '설정 없음';
  return v.enabled ? '켜짐' : '꺼짐';
}

/** 결과 한 줄 — 무엇이 일어났고 **다음에 무엇을 보면 되는지**까지. */
function ResultLine({ result, employeeId }: { result: HrProbeResult; employeeId: string }): JSX.Element {
  switch (result.kind) {
    case 'found': {
      const { name, department, position } = result.employee;
      const extra = [department, position].filter((x): x is string => !!x).join(' · ');
      return (
        <>
          <CheckCircle size={16} aria-hidden="true" />
          <span className="sysmail__ok">
            찾았습니다 — {name}{extra !== '' ? ` (${extra})` : ''}.
          </span>
          <span> 연결·인증이 정상입니다.</span>
        </>
      );
    }
    case 'not_found':
      return (
        <>
          <CheckCircle size={16} aria-hidden="true" />
          <span>
            <b>HR 에 사원번호 {employeeId} 이(가) 없습니다.</b> 연결·인증은 정상입니다 — 사원번호를 확인하세요.
          </span>
        </>
      );
    case 'auth':
      return (
        <>
          <XCircle size={16} aria-hidden="true" />
          <span>
            <span className="sysmail__bad">HR 이 인증을 거절했습니다.</span> API 키가 맞는지, 만료되지 않았는지 확인하세요.
          </span>
        </>
      );
    case 'unavailable':
      return (
        <>
          <XCircle size={16} aria-hidden="true" />
          <span>
            <span className="sysmail__bad">HR 에 닿지 못했습니다.</span> 주소·방화벽·제한 시간을 확인하세요. HR 쪽이 잠시 멈췄을 수도 있습니다.
          </span>
        </>
      );
    case 'not_configured':
      return (
        <>
          <AlertTriangle size={16} aria-hidden="true" />
          <span>
            <b>HR 연동이 설정되지 않았거나 꺼져 있습니다.</b> 이 상태에서는 사원 조회를 하지 않습니다.
          </span>
        </>
      );
  }
}

export function HrPreflightSection(): JSX.Element {
  const [config, setConfig] = useState<HrIntegrationView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [employeeId, setEmployeeId] = useState('');
  const [busy, setBusy] = useState(false);
  const [probe, setProbe] = useState<{ result: HrProbeResult; employeeId: string } | null>(null);
  const [probeError, setProbeError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      setConfig(await fetchHrIntegration());
    } catch {
      setConfig(null);
      setLoadError('HR 연동 설정을 불러오지 못했습니다.');
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const trimmed = employeeId.trim();

  const runProbe = async (e?: FormEvent): Promise<void> => {
    e?.preventDefault();
    if (trimmed === '' || busy) return;
    setBusy(true);
    setProbe(null);
    setProbeError(null);
    try {
      setProbe({ result: await probeHrEmployee(trimmed), employeeId: trimmed });
    } catch (err) {
      /* 429 는 권한·네트워크 문제가 아니다 — 기다리면 풀린다고 따로 말한다(#1317 리뷰). */
      const status = (err as { response?: { status?: number } })?.response?.status;
      setProbeError(status === 429
        ? '요청이 너무 잦습니다 — 잠시 뒤 다시 시도하세요.'
        : '점검하지 못했습니다 — 권한이나 네트워크를 확인한 뒤 다시 시도하세요.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ConsoleListScreen
      title="HR 연동 사전 점검"
      kpi={kpiOf(config)}
      desc="사원번호 하나로 이 회사의 HR 연동이 실제로 답하는지 확인합니다. 설정은 바꾸지 않습니다 — 지금 값을 읽어 보여 주고, 조회 시험만 합니다."
      toolsExtra={(
        <button type="button" className="svc-btn" disabled={busy} onClick={() => { void load(); }}>
          <RefreshCw size={14} aria-hidden="true" /> 다시 읽기
        </button>
      )}
      notice={loadError !== null ? (
        <div className="admin-console__alert" role="alert">
          <AlertTriangle size={16} aria-hidden="true" /><span>{loadError}</span>
        </div>
      ) : undefined}
    >
      <div className="sysmail">
        {/* ── 지금 설정 — 읽기만 ─────────────────────────────────────────── */}
        <section className="sysmail__sec" aria-labelledby="hrprobe-config-h">
          <h3 className="sysmail__h" id="hrprobe-config-h">지금 설정 (읽기 전용)</h3>
          {config === null ? (
            <p className="cl-empty">{loadError === null ? '읽는 중…' : '보여 줄 것이 없습니다.'}</p>
          ) : !config.configured ? (
            <p className="sysmail__hint">
              <b>설정이 없습니다.</b> 이 회사는 HR 연동을 쓰지 않습니다 — 아래 점검은 「설정되지 않음」으로 답합니다.
            </p>
          ) : (
            <dl className="hrprobe__dl">
              <dt>어댑터</dt>
              <dd>{ADAPTER_LABEL[config.adapter] ?? config.adapter} <code>{config.adapter}</code></dd>
              <dt>주소 (base_url)</dt>
              <dd>{config.baseUrl ? <code>{config.baseUrl}</code> : '—'}</dd>
              <dt>사용 (enabled)</dt>
              <dd>{config.enabled ? '켜짐' : '꺼짐 — 점검해도 조회하지 않습니다'}</dd>
              <dt>API 키</dt>
              <dd>{config.apiKeyMasked ?? '없음'}</dd>
              <dt>제한 시간</dt>
              <dd>{config.timeoutSec}초</dd>
            </dl>
          )}
          <p className="sysmail__hint">
            로그인한 콘솔 계정이 속한 회사의 설정입니다. 설정을 바꾸는 곳은 이 화면이 아닙니다.
          </p>
        </section>

        {/* ── 사원번호로 점검 ───────────────────────────────────────────── */}
        <section className="sysmail__sec" aria-labelledby="hrprobe-probe-h">
          <h3 className="sysmail__h" id="hrprobe-probe-h">사원번호로 점검</h3>
          <p className="sysmail__hint">
            HR 에서 사원 한 명을 실제로 조회해 봅니다. 아무것도 저장하지 않으며, 감사 기록에는 사원번호 앞 두 자리만 남습니다.
          </p>
          <form className="hrprobe__form" onSubmit={(e) => { void runProbe(e); }}>
            <label className="cl-field">
              <span>사원번호</span>
              <input
                id="hrprobe-employee-id"
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                maxLength={64}
                placeholder="예: E1001"
                autoComplete="off"
              />
            </label>
            <button type="submit" className="svc-btn svc-btn--primary" disabled={busy || trimmed === ''}>
              <Search size={14} aria-hidden="true" /> {busy ? '점검 중…' : '점검'}
            </button>
          </form>

          <div aria-live="polite">
            {probeError !== null && (
              <div className="admin-console__alert" role="alert">
                <AlertTriangle size={16} aria-hidden="true" /><span>{probeError}</span>
              </div>
            )}
            {probe !== null && (
              <p className="hrprobe__result" role="status" data-kind={probe.result.kind}>
                <ResultLine result={probe.result} employeeId={probe.employeeId} />
              </p>
            )}
          </div>
        </section>
      </div>
    </ConsoleListScreen>
  );
}
